import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faChevronDoubleUp, faRight } from '@fortawesome/pro-solid-svg-icons';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Lightning } from '@sorare/core/src/atoms/icons/Lightning';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';
import { SplitProgressBar } from '@sorare/core/src/components/collections/SplitProgressBar';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { ensureHexColor, isLightColor } from '@sorare/core/src/lib/colors';

import { MissingCardsForCollection } from '../MissingCardsForCollection';
import type {
  LazyCollectionElement_CardCollectionScoreIncrease,
  LazyCollectionUpsellElementQuery,
  LazyCollectionUpsellElementQueryVariables,
} from './__generated__/index.graphql';

const Container = styled(Vertical)`
  padding: 0 var(--double-unit);
`;

const CollectionProgress = styled(Vertical)`
  padding: var(--intermediate-unit);
  border-radius: var(--unit);
  background-color: var(--background-color);
`;

const Shine = styled.span`
  text-shadow: 0px 1px 7px var(--c-yellow-400);
  filter: drop-shadow(0px 1px 7px var(--c-yellow-400));
`;

const LAZY_COLLECTION_UPSELL_ELEMENT_QUERY = gql`
  query LazyCollectionUpsellElementQuery($slug: String!, $userSlug: String!) {
    cardCollection(slug: $slug) {
      slug
      anyTeam {
        slug
        styleGuideColor
        ...TeamAvatar_team
      }
      userCardCollection(forUserSlug: $userSlug) {
        id
        ...MissingCardsForCollection_userCardCollection
        ...SplitProgressBar_userCardCollection
      }
      ...SplitProgressBar_cardCollection
      ...MissingCardsForCollection_cardCollection
    }
  }
  ${TeamAvatar.fragments.team}
  ${SplitProgressBar.fragments.cardCollection}
  ${SplitProgressBar.fragments.userCardCollection}
  ${MissingCardsForCollection.fragments.userCardCollection}
  ${MissingCardsForCollection.fragments.cardCollection}
` as TypedDocumentNode<
  LazyCollectionUpsellElementQuery,
  LazyCollectionUpsellElementQueryVariables
>;

type Props = {
  cardCollectionScoreIncrease: LazyCollectionElement_CardCollectionScoreIncrease;
};

export const LazyCollectionElement = ({
  cardCollectionScoreIncrease,
}: Props) => {
  const { currentUser } = useCurrentUserContext();

  const {
    scoreIncrease,
    bonusIncrease,
    cardCollection: { slug },
  } = cardCollectionScoreIncrease;

  const { data } = useQuery(LAZY_COLLECTION_UPSELL_ELEMENT_QUERY, {
    variables: {
      slug,
      userSlug: currentUser!.slug,
    },
    skip: !slug || !currentUser?.slug,
  });

  const cardCollection = data?.cardCollection;

  const { styleGuideColor } = cardCollection?.anyTeam || {};
  const backgroundColor =
    styleGuideColor && !isLightColor(ensureHexColor(styleGuideColor))
      ? styleGuideColor
      : 'var(--c-nl-700-opaque)';

  const { userCardCollection } = cardCollection || {};
  const { bonus = 0 } = userCardCollection || {};

  const bonusIncreased = bonusIncrease + bonus;
  return (
    <>
      <Container>
        <CollectionProgress
          style={
            { '--background-color': backgroundColor } as React.CSSProperties
          }
        >
          <Horizontal>
            <TeamAvatar size={20} team={cardCollection?.anyTeam} />
            <BodyM bold>
              {userCardCollection?.score}{' '}
              <BodyM as="span" bold color="var(--c-yellow-400)">
                +{scoreIncrease} <Lightning />
              </BodyM>
            </BodyM>
            <Chip color="yellow" size="medium">
              <Horizontal as="span" gap={0.5}>
                <span>+{bonus * 100}%</span>
                {bonusIncreased > bonus && (
                  <>
                    <FontAwesomeIcon
                      icon={faRight}
                      color="var(--c-yellow-400)"
                    />
                    <Shine>+{bonusIncreased * 100}%</Shine>
                    <Shine>
                      <FontAwesomeIcon icon={faChevronDoubleUp} />
                    </Shine>
                  </>
                )}
              </Horizontal>
            </Chip>
          </Horizontal>
          <SplitProgressBar
            cardCollection={cardCollection}
            userCardCollection={userCardCollection}
            scoreIncrease={scoreIncrease}
            withoutLabel
          />
        </CollectionProgress>
      </Container>
      <MissingCardsForCollection
        userCardCollection={userCardCollection}
        cardCollection={cardCollection}
      />
    </>
  );
};

LazyCollectionElement.fragments = {
  cardCollectionScoreIncrease: gql`
    fragment LazyCollectionElement_CardCollectionScoreIncrease on CardCollectionScoreIncrease {
      id
      scoreIncrease
      bonusIncrease
      cardCollection {
        slug
      }
    }
  ` as TypedDocumentNode<LazyCollectionElement_CardCollectionScoreIncrease>,
};
